// Banger's design tokens (docs/design/README.md). The shell's values mirror style.css's :root; zones override only the
// palette, type, texture and motion, and only inside their own root (`[data-zone]`), so the global bar and footer keep
// the shell's look on every page. public/zones.css reads the custom properties zoneVars() sets.

import type { Motion, Palette, Texture, Zone } from "./zones/spec.ts";
import { SHELL_PALETTE } from "./zones/spec.ts";

/** Type scale (rem), a 1.25 ratio from 1rem, with fluid display sizes. */
export const TYPE_SCALE = {
  micro: "0.75rem",
  small: "0.875rem",
  body: "1rem",
  lead: "1.125rem",
  h3: "1.25rem",
  h2: "clamp(1.4rem, 1.1rem + 1.2vw, 1.95rem)",
  h1: "clamp(2rem, 1.4rem + 2.6vw, 3.05rem)",
  display: "clamp(2.6rem, 1.6rem + 5vw, 5.6rem)",
} as const;

/** Spacing steps (px), 4-based. */
export const SPACE = [0, 4, 8, 12, 16, 24, 32, 48, 64, 96, 128] as const;

/** Depth: the shell's three surfaces and the shadows that lift a layer. */
export const DEPTH = {
  flat: "none",
  raised: "0 1px 0 rgba(255,255,255,.04) inset, 0 8px 24px rgba(0,0,0,.35)",
  floating: "0 1px 0 rgba(255,255,255,.06) inset, 0 24px 64px rgba(0,0,0,.55)",
} as const;

/**
 * Type presets. System stacks only: no webfont download stands between a zone and its first paint (budget: first
 * content < 1 s). A preset may later gain a bundled, subset woff2 (≤ 40 KB) at the head of its stack.
 */
export const TYPE_STACKS: Record<string, string> = {
  grotesk: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  condensed: '"Avenir Next Condensed", "Arial Narrow", "Roboto Condensed", "Helvetica Neue", sans-serif-condensed, sans-serif',
  serif: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif',
  gothic: '"Copperplate", "Copperplate Gothic Light", "Trajan Pro", "Cinzel", "Iowan Old Style", Georgia, serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, "Roboto Mono", monospace',
  rounded: 'ui-rounded, "SF Pro Rounded", "Nunito", "Varela Round", ui-sans-serif, system-ui, sans-serif',
};

/** Display treatment per preset: weight, tracking and case. */
export const DISPLAY_STYLE: Record<string, { weight: number; tracking: string; transform: string }> = {
  grotesk: { weight: 850, tracking: "-0.03em", transform: "none" },
  condensed: { weight: 800, tracking: "-0.01em", transform: "uppercase" },
  serif: { weight: 600, tracking: "-0.015em", transform: "none" },
  gothic: { weight: 400, tracking: "0.06em", transform: "uppercase" },
  mono: { weight: 700, tracking: "-0.02em", transform: "none" },
  rounded: { weight: 800, tracking: "-0.02em", transform: "none" },
};

export interface MotionPreset {
  /** base duration of an entrance (ms) */
  enter: number;
  /** delay between staggered items (ms) */
  stagger: number;
  /** entrance travel (px) */
  rise: number;
  ease: string;
  /** the hero's idle drift: a slow scale/pan loop (s), 0 for none */
  drift: number;
  /** what an entrance does besides fading */
  style: "none" | "rise" | "blur";
}

/** Motion presets. `still` is what every preset becomes under prefers-reduced-motion. */
export const MOTION: Record<Motion, MotionPreset> = {
  still: { enter: 0, stagger: 0, rise: 0, ease: "linear", drift: 0, style: "none" },
  calm: { enter: 520, stagger: 60, rise: 12, ease: "cubic-bezier(.22, 1, .36, 1)", drift: 40, style: "rise" },
  kinetic: { enter: 340, stagger: 40, rise: 28, ease: "cubic-bezier(.16, 1, .3, 1)", drift: 18, style: "rise" },
  eerie: { enter: 1100, stagger: 140, rise: 6, ease: "cubic-bezier(.45, 0, .2, 1)", drift: 60, style: "blur" },
};

/** Page transition between routes (the shell's): a short cross-fade, none under reduced motion. */
export const PAGE_TRANSITION = { out: 120, in: 220, ease: "cubic-bezier(.22, 1, .36, 1)" } as const;

export const TEXTURES: readonly Texture[] = ["none", "grain", "fog", "scanlines", "paper", "stars", "halftone"];

/** Budgets the shell enforces and the screenshot harness asserts (ms). */
export const BUDGETS = { firstContent: 1000, hero: 2500 } as const;

export function reducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** "#rrggbb" + alpha → "rgba(…)". */
export function alpha(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/** Mix two "#rrggbb" colours (t = share of b). */
export function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (s: number) => Math.round(((pa >> s) & 255) * (1 - t) + ((pb >> s) & 255) * t);
  return `#${((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1)}`;
}

/** The custom properties a zone root carries. The shell's own names (--bg, --text, --dim, --ruby…) are remapped too, so shell components inside a zone pick up its palette. */
export function zoneVars(z: Pick<Zone, "theme">, reduce = reducedMotion()): Record<string, string> {
  const p: Palette = { ...SHELL_PALETTE, ...z.theme.palette };
  const surface = p.surface ?? mix(p.bg, p.ink, 0.06);
  const dim = p.dim ?? mix(p.ink, p.bg, 0.4);
  const m = MOTION[reduce ? "still" : z.theme.motion];
  const d = DISPLAY_STYLE[z.theme.type.display] ?? DISPLAY_STYLE.grotesk!;
  return {
    "--z-bg": p.bg,
    "--z-ink": p.ink,
    "--z-accent": p.accent,
    "--z-dim": dim,
    "--z-surface": surface,
    "--z-line": alpha(p.ink, 0.12),
    "--z-glow": alpha(p.accent, 0.35),
    "--z-font-display": TYPE_STACKS[z.theme.type.display] ?? TYPE_STACKS.grotesk!,
    "--z-font-body": TYPE_STACKS[z.theme.type.body] ?? TYPE_STACKS.grotesk!,
    "--z-display-weight": String(d.weight),
    "--z-display-tracking": d.tracking,
    "--z-display-case": d.transform,
    "--z-enter": `${m.enter}ms`,
    "--z-stagger": `${m.stagger}ms`,
    "--z-rise": `${m.rise}px`,
    "--z-ease": m.ease,
    "--z-drift": m.drift ? `${m.drift}s` : "0s",
    // the shell's names, remapped inside the zone
    "--bg": p.bg,
    "--panel": surface,
    "--card": mix(surface, p.ink, 0.04),
    "--hover": mix(surface, p.ink, 0.09),
    "--border": alpha(p.ink, 0.14),
    "--text": p.ink,
    "--dim": dim,
    "--ruby": p.accent,
  };
}
